import { useCallback, useEffect, useMemo, useState } from 'react';
import { VistoryService } from '../services/vistory-service';
import { groupVistoriesByStatus, countByStatus } from '../utils/vistory-grouping';
import type { Vistory } from '../types/vistory.types';
import type { VistoryGroups, StatusCounts } from '@/features/vistories/utils/vistory-grouping';

interface UseVistoryDataResult {
  vistories: Vistory[];
  groups: VistoryGroups;
  counts: StatusCounts;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * Loads the vistories for a lease with loading/error state, refetching,
 * and derived grouping/counts by status.
 */
export function useVistoryData(
  agencyId: string | undefined,
  leaseId: string | undefined,
): UseVistoryDataResult {
  const [vistories, setVistories] = useState<Vistory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [fetchId, setFetchId] = useState(0);

  const refetch = useCallback(() => setFetchId((n) => n + 1), []);

  useEffect(() => {
    if (!agencyId || !leaseId) return;
    setIsLoading(true);
    setIsError(false);

    VistoryService.listVistories(agencyId, leaseId)
      .then((res) => setVistories(Array.isArray(res?.items) ? res.items : []))
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false));
  }, [agencyId, leaseId, fetchId]);

  const groups = useMemo(() => groupVistoriesByStatus(vistories), [vistories]);
  const counts = useMemo(() => countByStatus(vistories), [vistories]);

  return { vistories, groups, counts, isLoading, isError, refetch };
}
